import { FIT_GRADE_UNIT, FIT_GRADE_UNKNOWN } from '@/public/features/problem/constants/fitGrade'

export const MATCH_RANK_SPOTS = 2

export interface MatchRank {
  id: string
  title: string
  fitGradeLabel: string | null
}

export function MatchTile({
  title,
  tags,
  ranks,
  className = '',
}: {
  title: string
  tags?: string[] | undefined
  ranks: MatchRank[]
  className?: string
}) {
  return (
    <div
      className={`flex w-full items-center justify-center overflow-hidden rounded-xl border-[0.483px] border-white-200 bg-[#f2f0f7] px-[15px] py-[26px] ${className}`}
    >
      <div className="relative h-[127px] w-[307.4px] shrink-0">
        <ProblemMarker title={title} tags={tags} />

        {ranks.length > 0 && (
          <svg
            className="pointer-events-none absolute top-[31.34px] left-[109.8px]"
            width="36.7461"
            height="72.1577"
            viewBox="0 0 36.7461 72.1577"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M36.7461 0.5H26.2603C22.9465 0.5 20.2603 3.18629 20.2603 6.5V65.6577C20.2603 68.9714 22.9465 71.6577 26.2603 71.6577H36.7461"
              stroke="#D5D0F0"
            />
            <path d="M20.3904 34.9902H0" stroke="#D5D0F0" />
          </svg>
        )}

        {ranks.length === 0 ? (
          <p className="absolute top-1/2 right-0 m-0 w-[161px] -translate-y-1/2 text-center text-body-sm text-black-100">
            아직 연관 해결 카드가 없어요.
          </p>
        ) : (
          ranks.slice(0, MATCH_RANK_SPOTS).map((rank, i) => (
            <RankBubble
              key={rank.id}
              rank={rank}
              order={i + 1}
              style={{ top: i === 0 ? 10.08 : 77.08, left: i === 0 ? 146.4 : 146.2 }}
            />
          ))
        )}
      </div>
    </div>
  )
}

function ProblemMarker({ title, tags }: { title: string; tags?: string[] | undefined }) {
  return (
    <div className="absolute top-0 left-0 h-[127px] w-[113px] overflow-hidden rounded-lg bg-[linear-gradient(136.68deg,#7437d8_7.25%,#3d1d72_88.41%)]">
      <span
        className="pointer-events-none absolute top-[73.11px] left-[79.88px] size-[55.5px] rounded-full bg-white-50/10"
        aria-hidden="true"
      />
      <span
        className="pointer-events-none absolute top-[73.11px] left-[93.65px] size-[41.73px] rounded-full bg-white-50/10"
        aria-hidden="true"
      />

      <div className="absolute top-5 left-[19.5px] flex w-[76px] flex-col items-start gap-[5px]">
        <span className="inline-flex items-center justify-center rounded-[19px] bg-[#e6dcfa] px-[7px] py-[3px] text-[11px] leading-[1.2] font-medium whitespace-nowrap text-[#501fa8]">
          시민문제
        </span>
        <p className="m-0 line-clamp-2 w-full text-body-sm leading-[1.32] font-semibold text-white-50">
          {title}
        </p>
      </div>

      {tags !== undefined && tags.length > 0 && (
        <div className="absolute inset-x-0 bottom-0 flex h-[30px] items-center justify-center gap-1 bg-white-50 px-1">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center justify-center rounded-[19px] px-[7px] py-[3px] text-caption leading-[1.2] font-medium whitespace-nowrap text-[#501fa8]"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function RankBubble({
  rank,
  order,
  style,
}: {
  rank: MatchRank
  order: number
  style: { top: number; left: number }
}) {
  return (
    <div
      className="absolute flex h-[46px] w-[161px] items-center gap-1 overflow-hidden rounded-[99px] bg-white-50/95 px-[5px]"
      style={style}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-[25px] bg-violet-600 text-[13px] leading-[1.2] font-semibold text-white-50">
        {order}위
      </span>
      <span className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1 text-center">
        <span className="w-full truncate text-[13px] leading-[1.2] font-medium text-black-500">
          {rank.title}
        </span>
        <span className="text-caption leading-[1.2] font-semibold text-white-700">
          {rank.fitGradeLabel === null
            ? FIT_GRADE_UNKNOWN
            : `${FIT_GRADE_UNIT} ${rank.fitGradeLabel}`}
        </span>
      </span>
    </div>
  )
}
